import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, FileText, Search, Info } from 'lucide-react';
import { useCreatePurchaseRequest, type PRLineItemInput } from '../../../api/mutations/purchasing.mutations';
import { useInventory, useIngredients } from '../../../api/queries/inventory.queries';
import { useBranches } from '../../../api/queries/settings.queries';
import { useAuthStore } from '../../../stores/auth.store';
import { useBranchStore } from '../../../stores/branch.store';

interface CreatePRModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreatePRModal({ onClose, onSuccess }: CreatePRModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { selectedBranchId } = useBranchStore();

  const [branchId, setBranchId] = useState(selectedBranchId || user?.branchId || '');
  const [urgency, setUrgency] = useState<'normal' | 'high' | 'urgent'>('normal');
  const [urgencyReason, setUrgencyReason] = useState('');
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<PRLineItemInput[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showIngredientSearch, setShowIngredientSearch] = useState(false);

  const { data: branches } = useBranches();
  const { data: inventory } = useInventory(branchId);
  const { data: ingredients } = useIngredients();
  const createPR = useCreatePurchaseRequest();

  // Create a map for quick ingredient lookup
  const ingredientMap = new Map(ingredients?.map(ing => [ing._id, ing]) || []);

  const filteredInventory = inventory?.filter((item) =>
    item.ingredientId.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const addItem = (ingredientId: string, _name: string, _baseUnit: string) => {
    if (items.some((item) => item.ingredientId === ingredientId)) {
      return; // Already added
    }

    // Get full ingredient details for purchasing info
    const ingredient = ingredientMap.get(ingredientId);
    const purchaseUnit = ingredient?.purchaseUnit || _baseUnit;
    const estimatedPrice = ingredient?.estimatedPricePerPurchaseUnit;

    setItems([
      ...items,
      {
        ingredientId,
        quantity: 1,
        unit: purchaseUnit,
        estimatedPrice,
        isFreeFormItem: false,
      },
    ]);
    setSearchQuery('');
    setShowIngredientSearch(false);
  };

  const addFreeFormItem = () => {
    setItems([
      ...items,
      {
        customItemName: '',
        customItemDescription: '',
        quantity: 1,
        unit: 'unit',
        isFreeFormItem: true,
      },
    ]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, updates: Partial<PRLineItemInput>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (items.length === 0) {
      alert(t('purchasing.addAtLeastOneItem'));
      return;
    }

    try {
      await createPR.mutateAsync({
        branchId,
        items,
        urgency,
        urgencyReason: urgency !== 'normal' ? urgencyReason : undefined,
        requestedDeliveryDate: requestedDeliveryDate || undefined,
        notes: notes || undefined,
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create PR:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  const totalEstimatedCost = items.reduce((sum, item) => sum + (item.estimatedPrice || 0) * item.quantity, 0);

  // Get relevant unit options based on ingredient's purchase unit configuration
  const getUnitOptions = (ingredientId: string, baseUnit: string) => {
    const ingredient = ingredientMap.get(ingredientId);
    const purchaseUnit = ingredient?.purchaseUnit;

    // Define unit families
    const weightUnits = ['g', 'kg'];
    const volumeUnits = ['ml', 'L'];
    const packagingUnits = ['unit', 'bag', 'box', 'bottle', 'carton', 'pack', 'can', 'piece', 'gallon'];

    // If ingredient has a purchase unit, prioritize it
    if (purchaseUnit) {
      // For weight-based ingredients with packaging purchase unit (e.g., sugar in kg)
      if (weightUnits.includes(baseUnit) && weightUnits.includes(purchaseUnit)) {
        return { units: weightUnits, purchaseUnit };
      }
      // For volume-based ingredients with packaging purchase unit (e.g., milk in carton)
      if (volumeUnits.includes(baseUnit) && packagingUnits.includes(purchaseUnit)) {
        return { units: [purchaseUnit, ...volumeUnits], purchaseUnit };
      }
      if (volumeUnits.includes(baseUnit) && volumeUnits.includes(purchaseUnit)) {
        return { units: volumeUnits, purchaseUnit };
      }
      // For weight-based ingredients with packaging (e.g., tea leaves in bag)
      if (weightUnits.includes(baseUnit) && packagingUnits.includes(purchaseUnit)) {
        return { units: [purchaseUnit, ...weightUnits], purchaseUnit };
      }
      // Default: show purchase unit first, then base unit
      return { units: [purchaseUnit, baseUnit].filter((v, i, a) => a.indexOf(v) === i), purchaseUnit };
    }

    // Fallback to base unit families
    if (weightUnits.includes(baseUnit)) {
      return { units: weightUnits, purchaseUnit: undefined };
    }
    if (volumeUnits.includes(baseUnit)) {
      return { units: volumeUnits, purchaseUnit: undefined };
    }
    return { units: [baseUnit, ...packagingUnits.filter(u => u !== baseUnit)], purchaseUnit: undefined };
  };

  // Get purchase unit info text (e.g., "1 carton = 1000ml")
  const getPurchaseUnitInfo = (ingredientId: string) => {
    const ingredient = ingredientMap.get(ingredientId);
    if (!ingredient?.purchaseUnit || !ingredient?.purchaseUnitSize) return null;
    return `1 ${ingredient.purchaseUnit} = ${ingredient.purchaseUnitSize}${ingredient.unit}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={handleKeyDown}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            <h2 className="text-xl font-bold">{t('purchasing.createPR')}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Branch & Urgency */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchasing.branch')} *</label>
                <select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  required
                >
                  <option value="">{t('common.select')}</option>
                  {branches?.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchasing.urgency')}</label>
                <select
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value as 'normal' | 'high' | 'urgent')}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                >
                  <option value="normal">{t('purchasing.normal')}</option>
                  <option value="high">{t('purchasing.high')}</option>
                  <option value="urgent">{t('purchasing.urgent')}</option>
                </select>
              </div>
            </div>

            {/* Urgency Reason */}
            {urgency !== 'normal' && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('purchasing.urgencyReason')} *
                </label>
                <input
                  type="text"
                  value={urgencyReason}
                  onChange={(e) => setUrgencyReason(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  placeholder={t('purchasing.urgencyReasonPlaceholder')}
                  required
                />
              </div>
            )}

            {/* Delivery Date */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('purchasing.requestedDeliveryDate')}
              </label>
              <input
                type="date"
                value={requestedDeliveryDate}
                onChange={(e) => setRequestedDeliveryDate(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">{t('purchasing.items')} *</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowIngredientSearch(true)}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    {t('purchasing.addIngredient')}
                  </button>
                  <button
                    type="button"
                    onClick={addFreeFormItem}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    {t('purchasing.addCustomItem')}
                  </button>
                </div>
              </div>

              {/* Ingredient Search */}
              {showIngredientSearch && (
                <div className="mb-4 p-4 rounded-lg border bg-secondary/50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('purchasing.searchIngredients')}
                      className="w-full pl-9 pr-3 py-2 rounded-lg border bg-background"
                      autoFocus
                    />
                  </div>
                  {searchQuery && (
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                      {filteredInventory.map((item) => (
                        <button
                          key={item._id}
                          type="button"
                          onClick={() => addItem(item.ingredientId._id, item.ingredientId.name, item.ingredientId.unit)}
                          className="w-full text-left px-3 py-2 rounded hover:bg-secondary text-sm"
                        >
                          {item.ingredientId.name} ({item.ingredientId.unit}) - {t('purchasing.inStock')}: {item.quantity}
                        </button>
                      ))}
                      {filteredInventory.length === 0 && (
                        <p className="text-sm text-muted-foreground p-2">{t('common.noResults')}</p>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowIngredientSearch(false)}
                    className="mt-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              )}

              {/* Item List */}
              <div className="space-y-2">
                {/* Column Headers */}
                {items.length > 0 && (
                  <div className="flex items-center gap-3 px-3 text-xs text-muted-foreground font-medium">
                    <span className="flex-1">{t('purchasing.ingredient')}</span>
                    <span className="w-20 text-center">{t('purchasing.qty')}</span>
                    <span className="w-24 text-center">{t('purchasing.unit')}</span>
                    <span className="w-28 text-center">{t('purchasing.unitPrice')}</span>
                    <span className="w-8" />
                  </div>
                )}
                {items.map((item, index) => {
                  const inventoryItem = inventory?.find((inv) => inv.ingredientId._id === item.ingredientId);
                  const baseUnit = item.isFreeFormItem ? 'unit' : inventoryItem?.ingredientId.unit || 'unit';
                  const ingredientId = item.ingredientId || '';
                  const { units } = getUnitOptions(ingredientId, baseUnit);
                  const purchaseInfo = !item.isFreeFormItem ? getPurchaseUnitInfo(ingredientId) : null;

                  return (
                    <div key={index} className="rounded-lg border">
                      <div className="flex items-center gap-3 p-3">
                        {item.isFreeFormItem ? (
                          <>
                            <input
                              type="text"
                              value={item.customItemName || ''}
                              onChange={(e) => updateItem(index, { customItemName: e.target.value })}
                              placeholder={t('purchasing.itemName')}
                              className="flex-1 rounded border bg-background px-2 py-1 text-sm"
                              required
                            />
                            <input
                              type="text"
                              value={item.customItemDescription || ''}
                              onChange={(e) => updateItem(index, { customItemDescription: e.target.value })}
                              placeholder={t('purchasing.description')}
                              className="flex-1 rounded border bg-background px-2 py-1 text-sm"
                            />
                          </>
                        ) : (
                          <span className="flex-1 text-sm font-medium">
                            {inventoryItem?.ingredientId.name || item.ingredientId}
                          </span>
                        )}
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, { quantity: parseFloat(e.target.value) || 0 })}
                          className="w-20 rounded border bg-background px-2 py-1 text-sm text-center"
                          min={0.1}
                          step={0.1}
                          required
                        />
                        <select
                          value={item.unit}
                          onChange={(e) => updateItem(index, { unit: e.target.value })}
                          className="w-24 rounded border bg-background px-2 py-1.5 text-sm"
                          required
                        >
                          {units.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={item.estimatedPrice || ''}
                          onChange={(e) => updateItem(index, { estimatedPrice: parseFloat(e.target.value) || 0 })}
                          placeholder={`฿/${item.unit}`}
                          className="w-28 rounded border bg-background px-2 py-1 text-sm"
                          min={0}
                          step={0.01}
                        />
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="p-1 text-destructive hover:bg-destructive/10 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {/* Purchase unit info hint */}
                      {purchaseInfo && (
                        <div className="px-3 pb-2 -mt-1">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Info className="h-3 w-3" />
                            {purchaseInfo}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {items.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                    <p className="text-sm">{t('purchasing.noItemsAdded')}</p>
                  </div>
                )}
              </div>

              {/* Total */}
              {items.length > 0 && totalEstimatedCost > 0 && (
                <div className="mt-3 text-right text-sm">
                  <span className="text-muted-foreground">{t('purchasing.estimatedTotal')}:</span>{' '}
                  <span className="font-bold">
                    {new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(totalEstimatedCost)}
                  </span>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2"
                rows={3}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={createPR.isPending || items.length === 0}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {createPR.isPending ? t('common.creating') : t('purchasing.submitPR')}
          </button>
        </div>
      </div>
    </div>
  );
}
